// ABOUTME: Edit-everything dialog for a hypothesis (#29): statement, signals CRUD, archive
// ABOUTME: Any save makes dependent evidence/analyses stale via the content hash — banners handle re-runs

import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { HypothesisFramework, SignalItem, hypothesisEditApi } from '@/api/analysis'

const KINDS: { kind: SignalItem['kind']; label: string }[] = [
  { kind: 'positive', label: 'Positive signals (supports)' },
  { kind: 'negative', label: 'Negative signals (refutes)' },
  { kind: 'ambiguity', label: 'Ambiguities' },
  { kind: 'expected_absence', label: 'Expected absences' },
]

type EditableSignal = { element_id: string | null; kind: SignalItem['kind']; text: string; localId: string }

let localIdCounter = 0
const nextLocalId = () => `local-${++localIdCounter}`

export function EditHypothesisDialog({ hypothesisId, open, onClose }: {
  hypothesisId: string
  open: boolean
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [initial, setInitial] = useState<HypothesisFramework | null>(null)
  const [statement, setStatement] = useState('')
  const [archived, setArchived] = useState(false)
  const [signals, setSignals] = useState<EditableSignal[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setInitial(null)
    setError(null)
    hypothesisEditApi.getFramework(hypothesisId).then((fw) => {
      setInitial(fw)
      setStatement(fw.statement)
      setArchived(fw.archived)
      setSignals(fw.signals.map((s) => ({ ...s, localId: s.element_id })))
    }).catch((e) => setError(e?.response?.data?.detail ?? 'Failed to load'))
  }, [open, hypothesisId])

  const invalidateAll = () => {
    // Content changed → hypothesis pages, readiness, and interview staleness all shift
    queryClient.invalidateQueries({ queryKey: ['hypothesis'] })
    queryClient.invalidateQueries({ queryKey: ['hypotheses'] })
    queryClient.invalidateQueries({ queryKey: ['hypothesis-readiness'] })
    queryClient.invalidateQueries({ queryKey: ['interviews'] })
  }

  const save = async () => {
    if (!initial) return
    setSaving(true)
    setError(null)
    try {
      if (statement.trim() !== initial.statement || archived !== initial.archived) {
        await hypothesisEditApi.patchHypothesis(hypothesisId, {
          statement: statement.trim() !== initial.statement ? statement.trim() : undefined,
          archived: archived !== initial.archived ? archived : undefined,
        })
      }
      const initialById = new Map(initial.signals.map((s) => [s.element_id, s]))
      // A blanked-out existing signal counts as removed, not silently reverted
      const effective = signals.filter((s) => s.text.trim())
      const currentIds = new Set(effective.map((s) => s.element_id).filter(Boolean))
      for (const s of initial.signals) {
        if (!currentIds.has(s.element_id)) await hypothesisEditApi.deleteSignal(s.element_id)
      }
      for (const s of effective) {
        if (s.element_id == null) {
          const created = await hypothesisEditApi.addSignal(hypothesisId, s.kind, s.text.trim())
          // Record the id immediately: if a LATER call fails, retrying won't re-create this one
          setSignals((all) => all.map((x) =>
            x.localId === s.localId ? { ...x, element_id: created.element_id } : x))
          s.element_id = created.element_id
        } else if (initialById.get(s.element_id)?.text !== s.text.trim()) {
          await hypothesisEditApi.editSignal(s.element_id, s.text.trim())
        }
      }
      invalidateAll()
      onClose()
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Save failed — retry applies only the remaining changes')
      // Partial changes may have committed: refresh the baseline from the server so a
      // retry diffs against reality, and let the rest of the app see what landed.
      invalidateAll()
      try {
        const fw = await hypothesisEditApi.getFramework(hypothesisId)
        setInitial(fw)
      } catch { /* keep the old baseline if the refetch itself fails */ }
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit hypothesis</DialogTitle>
          <DialogDescription>
            Changes to the statement or signals mark existing evidence and analyses as stale —
            the interviews and analysis pages will offer the re-runs.
          </DialogDescription>
        </DialogHeader>

        {error && <p className="text-sm text-red-600">{error}</p>}

        {!initial ? (
          <p className="text-sm text-gray-500 py-6 text-center">Loading…</p>
        ) : (
          <div className="space-y-5">
            <label className="block">
              <span className="block text-sm font-medium text-gray-700 mb-1">Statement</span>
              <Textarea value={statement} onChange={(e) => setStatement(e.target.value)} rows={3} />
            </label>

            {KINDS.map(({ kind, label }) => (
              <div key={kind}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => setSignals((s) => [...s, { element_id: null, kind, text: '', localId: nextLocalId() }])}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                <div className="space-y-1.5">
                  {signals.filter((s) => s.kind === kind).map((s) => {
                    const idx = signals.indexOf(s)
                    return (
                      <div key={s.localId} className="flex items-center gap-1.5">
                        <Input
                          value={s.text}
                          onChange={(e) => setSignals((all) =>
                            all.map((x, j) => (j === idx ? { ...x, text: e.target.value } : x)))}
                          placeholder="Signal text…"
                        />
                        <Button
                          size="icon" variant="ghost" aria-label="Remove signal"
                          onClick={() => setSignals((all) => all.filter((_, j) => j !== idx))}
                        >
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    )
                  })}
                  {signals.filter((s) => s.kind === kind).length === 0 && (
                    <p className="text-xs text-gray-400">None</p>
                  )}
                </div>
              </div>
            ))}

            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
              Archived — kept for history, excluded from extraction and readiness
            </label>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !initial || !statement.trim()}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
